import * as React from 'react';

// @ts-ignore
import * as AutoMessageActions from 'admin/actions/AutoMessageActions';
import { CSVPreviewWidget } from 'components/File';
import { ParsedResults } from 'components/File/CSVParser';
import Heading from 'components/Heading';
// @ts-ignore
import List from 'components/List';
// @ts-ignore
import ListItem from 'components/ListItem';
import MediaObject from 'components/MediaObject';
import Row from 'components/Row';
import Select, { Option } from 'components/Select';
import Text from 'components/Text';
import EventTracking from 'lib/EventTracking';
import UserSMSDevice from 'user/components/UserSMSDevice';

const STUDENT_NUMBER_HEADER = 'student_number';

export type Props = {
  hasCustomizedMessaging: boolean;
  rowToParse: number;
  parsedResults?: ParsedResults;
  onRowChange: (rowToParse: number) => void;
  onCSVParse: (parsedResults: ParsedResults) => void;
  messageBody?: string;
  fileAttachmentUuids?: string[];
  customMessageFields: string[];
};

export default function AdminNewAutoMessageReviewTestMessage(props: Props) {
  const {
    hasCustomizedMessaging,
    rowToParse,
    parsedResults,
    onRowChange,
    onCSVParse,
    messageBody,
    fileAttachmentUuids,
    customMessageFields,
  } = props;

  const renderWithoutCustomizedMessaging = () => (
    <Text element="p">
      Messages will not be sent until you activate this auto message. You can save this auto message
      as a draft and transfer a CSV file to Remind to test for errors before activating it.
    </Text>
  );

  const renderWithCustomizedMessaging = () => (
    <List indented size="xlarge" data-test="customized-messaging-test-experience">
      {customMessageFields.length > 0 ? (
        <ListItem>
          <Row size="small">
            <Text>Upload test data</Text>
            <Text size="xsmall" color="thunder">
              <Text element="div">
                Make sure the CSV you upload includes the following columns:
              </Text>
              {[STUDENT_NUMBER_HEADER, ...customMessageFields].map(field => (
                <React.Fragment key={field}>
                  <Text element="code">{field}</Text>{' '}
                </React.Fragment>
              ))}
            </Text>
          </Row>
          <Row size="small">
            <CSVPreviewWidget
              onSuccess={trackSuccessfulCSVParse}
              onError={trackUnsuccessfulCSVParse}
              onParse={onCSVParse}
              validColumnNames={[STUDENT_NUMBER_HEADER, ...customMessageFields]}
            />
          </Row>
          {parsedResults && (
            <MediaObject center gutter="small">
              <Text>Use data from row </Text>
              <Select
                data-test="change-row-dropdown"
                inline
                value={rowToParse}
                onChange={onRowChange}
                options={getRowOptions(parsedResults.parsedRowData.length)}
              />
            </MediaObject>
          )}
        </ListItem>
      ) : null}
      <ListItem>
        <Row size="small">
          <Text>Confirm your information:</Text>
        </Row>
        <UserSMSDevice
          actionButtonAttributes={{
            dataTestLabel: 'send-test-message-button',
            onClick: (deviceUuid?: string) => {
              AutoMessageActions.sendTestMessage(
                messageBody || '',
                fileAttachmentUuids || [],
                deviceUuid
              );
              trackEvent('send_test_message.cta.click');
            },
            label: 'Send message',
          }}
        />
      </ListItem>
    </List>
  );

  return (
    <React.Fragment>
      <Heading level="3" levelStyle="4">
        2. Send a test message (optional)
        <Text size="xsmall" color="thunder">
          <Text element="div">Attachments won't be available to access from a test message.</Text>
        </Text>
      </Heading>
      {hasCustomizedMessaging
        ? renderWithCustomizedMessaging()
        : renderWithoutCustomizedMessaging()}
    </React.Fragment>
  );
}

const eventPrefix = 'auto_messaging.review';
function trackEvent(event: string) {
  EventTracking.trackKey(`${eventPrefix}.${event}`);
}

function trackSuccessfulCSVParse() {
  trackEvent('upload_test_data.success');
}

function trackUnsuccessfulCSVParse() {
  trackEvent('upload_test_data.error');
}

function getRowOptions(numberOfRows: number): Option[] {
  return new Array(numberOfRows).fill(null).map<Option>((_, index) => {
    return {
      label: index.toString(),
      value: index.toString(),
    };
  });
}
